// src/components/TaskForm.js
import { useRef, useState, useEffect } from "react";
import { CATEGORIES, PRIORITIES, RECURRENCE, CAT_ICONS, STORES } from "../constants";
import { useEnabledStores } from "./StoreSettings";

import { GROQ_API_KEY, GROQ_MODEL } from "../config";

async function callGroq(prompt) {
  const resp = await fetch("https://api.groq.com/openai/v1/chat/completions", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${GROQ_API_KEY}`,
    },
    body: JSON.stringify({
      model: GROQ_MODEL,
      max_tokens: 1000,
      temperature: 0.7,
      messages: [{ role: "user", content: prompt }],
    }),
  });
  const data = await resp.json();
  if (data.error) throw new Error(data.error.message);
  return data.choices?.[0]?.message?.content || "";
}

// Compress images before saving
function compressImage(file, maxDimension = 800, quality = 0.7) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        let { width, height } = img;
        if (width > maxDimension || height > maxDimension) {
          if (width > height) {
            height = Math.round((height * maxDimension) / width);
            width = maxDimension;
          } else {
            width = Math.round((width * maxDimension) / height);
            height = maxDimension;
          }
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Post-process AI parts list to fix known calculation errors
// ─────────────────────────────────────────────────────────────────────────────

function extractSqft(desc) {
  if (!desc) return null;
  const patterns = [
    // "2455 sqft", "2455 sq ft", "2,455 square feet"
    /([0-9][0-9,]*)\s*(?:sq\.?\s*ft\.?|square\s*feet|sqft|sf)(?![a-z])/i,
    // "sqft is 2455", "square feet is 2455", "total sqft of X is 2455"
    /(?:sq\.?\s*ft\.?|square\s*feet|sqft)\s+(?:is|of|=|:)?\s*([0-9][0-9,]*)/i,
    // "is 2455 sqft", "is 2455 square"  
    /is\s+([0-9][0-9,]*)\s*(?:sq\.?\s*ft|square|sqft)/i,
    // "total of 2455", "area of 2455", "roof of 2455"
    /(?:total|area|roof|floor|room)\s+(?:is|of|=|:)?\s*([0-9][0-9,]*)/i,
  ];
  for (const pattern of patterns) {
    const match = desc.match(pattern);
    if (match) {
      // Return whichever capture group has the number
      const num = match[1] || match[2];
      if (num) return parseFloat(num.replace(/,/g, ''));
    }
  }
  return null;
}

const COVERAGE_TABLE = [
  { keywords: ["ridge cap", "ridge shingle"], perLinearFt: 35, unit: "bundle"  }, // 1 bundle = 35 linear feet of ridge
  { keywords: ["shingle"],                  coverage: 32.8, unit: "bundle"  },
  { keywords: ["underlayment", "felt",
               "synthetic felt"],           coverage: 400,  unit: "roll"    },
  { keywords: ["ice and water", "ice &"],   coverage: 75,   unit: "roll"    },
  { keywords: ["osb", "roof deck",
               "roof sheathing"],           coverage: 32,   unit: "sheet"   },
  { keywords: ["plywood"],                  coverage: 32,   unit: "sheet"   },
  { keywords: ["drywall", "sheetrock"],     coverage: 32,   unit: "sheet"   },
  { keywords: ["roofing nail"],             perSquare: 2.5, unit: "lb"      },
  { keywords: ["hardwood floor",
               "laminate floor"],           coverage: 20,   unit: "box"     },
  { keywords: ["vinyl plank", "lvp"],       coverage: 22,   unit: "box"     },
  { keywords: ["floor underlayment"],       coverage: 100,  unit: "roll"    },
  { keywords: ["thinset", "tile adhesive"], coverage: 45,   unit: "bag"     },
  { keywords: ["grout"],                    coverage: 50,   unit: "bag"     },
  { keywords: ["interior paint"],           coverage: 375,  unit: "gallon"  },
  { keywords: ["exterior paint"],           coverage: 275,  unit: "gallon"  },
  { keywords: ["primer"],                   coverage: 350,  unit: "gallon"  },
];

function extractLinearFt(desc) {
  if (!desc) return null;
  // Only extract linear feet from ridge/linear context — not general measurements
  // Check if the text mentions ridge, linear, or length in context
  const ridgeContext = /ridge|linear|length|long|peak|hip/i.test(desc);
  if (!ridgeContext) return null;

  const patterns = [
    // "45 linear feet", "45 lin ft"
    /([0-9][0-9,]*)\s*(?:linear\s*f(?:ee|oo)?t|lin\.?\s*ft\.?|linear feet)/i,
    // "45 feet", "45 ft", "45'"
    /([0-9][0-9,]*)\s*(?:feet|foot|ft\.?|')/i,
    // "is 45", "about 45", "approximately 45", "around 45", "maybe 45"
    /(?:is|about|approximately|around|maybe|roughly)\s+([0-9][0-9,]*)/i,
  ];

  for (const pattern of patterns) {
    const match = desc.match(pattern);
    if (match) {
      const num = parseFloat((match[1] || match[2] || '').replace(/,/g, ''));
      // Sanity check: ridge lines are typically 10-200 feet
      if (num >= 10 && num <= 200) return num;
    }
  }
  return null;
}

function correctPartQuantities(parts, desc) {
  const sqft = extractSqft(desc);
  const linearFt = extractLinearFt(desc);
  if (!sqft && !linearFt) return parts;

  return parts.map(part => {
    const nameLower = (part.name || "").toLowerCase();

    for (const spec of COVERAGE_TABLE) {
      const matched = spec.keywords.some(kw => nameLower.includes(kw));
      if (!matched) continue;

      let correctedQty;
      if (spec.perLinearFt && linearFt) {
        correctedQty = Math.ceil(linearFt / spec.perLinearFt);
      } else if (spec.perSquare && sqft) {
        correctedQty = Math.ceil((sqft / 100) * spec.perSquare);
      } else if (spec.coverage && sqft) {
        correctedQty = Math.ceil(sqft / spec.coverage);
      } else {
        break;
      }

      const aiQty = parseFloat(part.qty) || 1;
      const percentOff = Math.abs(aiQty - correctedQty) / correctedQty;
      if (percentOff > 0.20) {
        return { ...part, qty: correctedQty, unit: spec.unit };
      }
      break;
    }
    return part;
  });
}


export default function TaskForm({ task, onSave, onCancel, saving }) {
  const [form, setForm] = useState({ ...task });
  const [loadingAI, setLoadingAI] = useState(false);
  const [aiTab, setAiTab] = useState("guide");
  const [aiError, setAiError] = useState("");
  const [compressing, setCompressing] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);

  // AI clarifying questions state
  const [aiStage, setAiStage] = useState("idle"); // idle | questioning | answering | done
  const [aiQuestions, setAiQuestions] = useState([]);
  const [aiAnswers, setAiAnswers] = useState({});

  const fileRef = useRef();
  const initialForm = useRef({ ...task });

  // Track if form has changed from initial state
  useEffect(() => {
    const changed =
      form.title !== initialForm.current.title ||
      form.notes !== initialForm.current.notes ||
      form.category !== initialForm.current.category ||
      form.priority !== initialForm.current.priority ||
      form.dueDate !== initialForm.current.dueDate ||
      form.recurrence !== initialForm.current.recurrence ||
      (form.images || []).length !== (initialForm.current.images || []).length;
    setIsDirty(changed);
  }, [form]);

  // Warn on browser back/refresh if dirty
  useEffect(() => {
    const handler = (e) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  function set(key, val) {
    setForm(f => ({ ...f, [key]: val }));
    // Reset AI stage when content changes so questions can re-trigger
    if (key === "title" || key === "notes" || key === "category") {
      setAiStage("idle");
    }
  }

  function handleCancel() {
    if (isDirty) {
      setShowUnsavedModal(true);
    } else {
      onCancel();
    }
  }

  async function handleImages(e) {
    const files = Array.from(e.target.files);
    setCompressing(true);
    try {
      const compressed = await Promise.all(files.map(f => compressImage(f)));
      set("images", [...(form.images || []), ...compressed]);
    } catch (err) {
      console.error("Image compression failed:", err);
    } finally {
      setCompressing(false);
    }
  }

  // Categories that require measurements for accurate parts calculation
  const AREA_CATEGORIES = ["Roofing", "Flooring", "Interior", "Exterior", "Structural"];
  const AREA_KEYWORDS = ["roof", "floor", "tile", "carpet", "drywall", "paint", "ceiling", "wall", "deck", "patio", "siding", "insulation"];

  // Check if description is vague OR missing measurements for area-based work
  function isVague() {
    const notes = (form.notes || "").trim();
    const title = (form.title || "").trim();
    const fullText = (title + " " + notes).toLowerCase();

    // Always ask questions if description is very short
    if (notes.length < 20 && title.length < 30) return true;

    const isRoofing = form.category === "Roofing" || fullText.includes("roof");
    const isAreaBased = AREA_CATEGORIES.includes(form.category) ||
      AREA_KEYWORDS.some(kw => fullText.includes(kw));

    // Inline sqft check - flexible patterns
    const sqftPatterns = [
      /[0-9][0-9,]*\s*(?:sq\.?\s*ft|square\s*feet|sqft)/i,
      /(?:sqft|square feet|sq ft)\s*(?:is|of|=|:)?\s*[0-9]/i,
      /(?:total|area|roof|floor)\s+(?:is|of)?\s*[0-9][0-9,]*/i,
    ];
    const hasSqft = sqftPatterns.some(p => p.test(fullText));

    // Inline linear feet check
    const hasLinearFt = /[0-9][0-9,]*\s*(?:linear\s*f(?:ee|oo)?t|lin\.?\s*ft|linear feet)/i.test(fullText);

    // Ask questions if area-based but sqft missing
    if (isAreaBased && !hasSqft) return true;

    // Ask questions if roofing but no ridge line length given
    if (isRoofing && !hasLinearFt) {
        return true;
    }

    return false;
  }

  async function generateAI() {
    if (!form.title.trim()) return;

    // If description is vague, ask clarifying questions first
    if (isVague() && (aiStage === "idle" || aiStage === "done")) {
      setLoadingAI(true);
      setAiError("");
      try {
        const fullDesc = `${form.title} ${form.notes || ""}`;
        const sqftFound = extractSqft(fullDesc);
        const linearFtFound = extractLinearFt(fullDesc);
        const isRoofingJob = form.category === "Roofing" || fullDesc.toLowerCase().includes("roof");
        const needsSqft = !sqftFound;
        const needsLinearFt = isRoofingJob && !linearFtFound;

        const fullDescForQ = `${form.title} ${form.notes || ""}`;
        const sqft = extractSqft(fullDescForQ);
        const linearFt = extractLinearFt(fullDescForQ);
        const questionsRaw = await callGroq(
          `You are a home repair expert assistant. A homeowner wants help with this repair task: "${form.title}" (Category: ${form.category}).
${form.notes ? `Their description: "${form.notes}"` : "Their description is missing."}

Generate 3 short, specific clarifying questions to help you give better repair advice and calculate materials accurately.

IMPORTANT — The user has already provided this information, DO NOT ask about it again:
${sqft ? `- Square footage: already provided (${sqft} sqft) — do NOT ask for sqft or area size` : ""}
${linearFt ? `- Ridge/linear length: already provided (${linearFt} linear feet) — do NOT ask for this` : ""}

Ask ONLY about information that is missing. Question priority:
${needsSqft ? `1. FIRST question MUST ask: "What is the total square footage of the area being repaired?"` : `1. Square footage is already known (${sqftFound} sqft) — do NOT ask about it again`}
${isRoofingJob && !linearFtFound ? `- Ask about ridge line length: "What is the approximate length of your roof ridge line in linear feet?"` : ""}
${isRoofingJob ? `- Ask whether any roof decking boards or rafters need replacement` : ""}
${form.category === "Flooring" ? `- Ask whether subfloor needs replacement and what type of subfloor exists` : ""}
- Ask about model numbers, age of materials, severity of damage, or anything specific to this repair
- Ask about what the user has already tried or existing conditions

Keep questions short, friendly, and do NOT repeat anything the user already told you.
Return ONLY a valid JSON array of exactly 3 strings. No markdown, no explanation. Example: ["Question 1?", "Question 2?", "Question 3?"]`
        );
        const cleaned = questionsRaw.replace(/```json|```/g, "").trim();
        const questions = JSON.parse(cleaned);
        setAiQuestions(questions);
        setAiAnswers(Object.fromEntries(questions.map((q, i) => [i, ""])));
        setAiStage("questioning");
      } catch (err) {
        // If question generation fails, log the error and generate directly
        await generateGuide("");
      } finally {
        setLoadingAI(false);
      }
      return;
    }

    // Generate with answers if we have them
    const extraContext = aiStage === "answering"
      ? aiQuestions.map((q, i) => `Q: ${q}\nA: ${aiAnswers[i] || "Not provided"}`).join("\n")
      : "";

    await generateGuide(extraContext);
  }

  async function generateGuide(extraContext) {
    setLoadingAI(true);
    setAiError("");
    setAiStage("idle");
    try {
      const desc = `${form.title}. Category: ${form.category}. Details: ${form.notes || "none provided"}${extraContext ? `\n\nAdditional context:\n${extraContext}` : ""}`;

      const [guide, partsRaw] = await Promise.all([
        callGroq(
          `You are a friendly, knowledgeable home repair expert. Give numbered, actionable steps. Be encouraging and safety-conscious. Include estimated time and skill level. Keep it under 600 words. Only provide the repair guide, and make sure to disclose at the top of every repair guide "IMPORTANT DISCLAIMER: This repair guide has been generated by artificial intelligence and is provided for informational purposes only. Homiefixit.com, its owners, and affiliates make no representations or warranties of any kind regarding the accuracy, completeness, or suitability of this information. Home repairs can be dangerous and may result in personal injury, death, or property damage if performed incorrectly. Always consult a licensed contractor or qualified professional before attempting any repairs. Check local building codes and obtain necessary permits before starting work. By using this guide you agree that Homiefixit.com is not liable for any damages, injuries, or losses resulting from your use of this information. Use at your own risk."

If the repair involves area-based materials (roofing, flooring, tile, drywall, paint), include a "Materials Calculation" section near the top that shows the math used to calculate quantities. This helps users verify the parts list is correct.

Generate a step-by-step home repair guide for: ${desc}`
        ),
        callGroq(
          `You are a precise home repair materials calculator. Return ONLY a valid JSON array with no markdown fences, no explanation — raw JSON only.

List the parts and materials needed for this home repair: ${desc}

CRITICAL CALCULATION RULES you MUST follow:
1. For area-based materials (shingles, flooring, tile, drywall, paint, underlayment, etc):
   - Extract the square footage from the task description
   - Divide by the coverage per unit to get base quantity
   - ALWAYS round UP to the nearest whole number (never round down)
   - Example: 2455 sqft roof, shingles cover 32.8 sqft/bundle → 2455 ÷ 32.8 = 74.8 → round up = 75 bundles
2. For length-based materials (lumber, pipe, wire, trim):
   - Calculate exact length needed then round up to standard sizes
3. For nails, screws and fasteners:
   - Roofing nails: 1 lb covers approximately 100 sqft of shingles. So 2455 sqft needs about 25 lbs of roofing nails
   - Drywall screws: approximately 1 lb per 150 sqft of drywall
   - Wood screws: estimate based on spacing, typically 1 lb per 50-100 linear feet
   - NEVER calculate nails per individual shingle then multiply — use the lbs per sqft coverage method
4. NEVER underestimate quantities — always round up, never round down
5. Use real-world product coverage specs in your calculations
6. Sanity check your answers — if a quantity seems unusually large, recalculate using a simpler coverage-based method

Each element must be: {"name":"string","qty":1,"unit":"each","estimatedCost":0.00,"searchTerm":"hardware store search term"}

The qty field must reflect your calculated quantity with waste factor applied.`
        ),
      ]);

      let parts = [];
      try {
        const cleaned = partsRaw.replace(/```json|```/g, "").trim();
        const rawParts = JSON.parse(cleaned);
        // Post-process parts to fix known AI calculation errors
        parts = correctPartQuantities(rawParts, desc);
      } catch (_) {}

      setForm(f => ({ ...f, repairGuide: guide, partsList: parts }));
      setAiTab("guide");
      setAiStage("done");
    } catch (err) {
      console.error("Groq error:", err);
      setAiError("AI generation failed. Please check your API key and try again.");
    } finally {
      setLoadingAI(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.title.trim()) return;
    onSave(form);
  }

  const hasAI = form.repairGuide || (form.partsList && form.partsList.length > 0);

  return (
    <>
      <form onSubmit={handleSubmit} style={{ maxWidth: 760 }} className="fade-in">
        {/* Save/Cancel buttons at TOP */}
        <div style={{ display: "flex", gap: 12, marginBottom: 20 }}>
          <button type="submit" className="btn-primary" style={{ flex: 1, fontSize: 15, padding: "13px" }} disabled={saving || compressing}>
            {saving ? "Saving..." : compressing ? "Compressing photos..." : "Save Task"}
          </button>
          <button type="button" className="btn-secondary" onClick={handleCancel}>Cancel</button>
        </div>
        <div style={styles.grid2}>
          <div style={{ gridColumn: "1/-1" }}>
            <label className="field-label">Task Title *</label>
            <input
              className="field-input"
              style={{ fontSize: 16, fontWeight: 700 }}
              placeholder="e.g. Fix leaking bathroom faucet"
              value={form.title}
              onChange={e => set("title", e.target.value)}
              required
            />
          </div>

          <div>
            <label className="field-label">Category</label>
            <select className="field-input" value={form.category} onChange={e => set("category", e.target.value)}>
              {CATEGORIES.map(c => <option key={c} value={c}>{CAT_ICONS[c]} {c}</option>)}
            </select>
          </div>

          <div>
            <label className="field-label">Priority</label>
            <select className="field-input" value={form.priority} onChange={e => set("priority", e.target.value)}>
              {PRIORITIES.map(p => <option key={p}>{p}</option>)}
            </select>
          </div>

          <div>
            <label className="field-label">Due Date</label>
            <input className="field-input" type="date" value={form.dueDate} onChange={e => set("dueDate", e.target.value)} />
          </div>

          <div>
            <label className="field-label">Recurring Schedule</label>
            <select className="field-input" value={form.recurrence} onChange={e => set("recurrence", e.target.value)}>
              {RECURRENCE.map(r => <option key={r}>{r}</option>)}
            </select>
          </div>

          <div style={{ gridColumn: "1/-1" }}>
            <label className="field-label">Describe the Issue</label>
            <textarea
              className="field-input"
              rows={4}
              style={{ resize: "vertical" }}
              placeholder="Be specific — the more detail, the better the AI repair guide! e.g. 'Hot water faucet drips constantly, single-handle mixer, about 10 years old, drip rate ~once per second.'"
              value={form.notes}
              onChange={e => set("notes", e.target.value)}
            />
          </div>
        </div>

        {/* Photos */}
        <div className="card" style={{ marginBottom: 16 }}>
          <label className="field-label">Photos of the Issue</label>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 10 }}>
            {(form.images || []).map((img, i) => (
              <div key={i} style={{ position: "relative" }}>
                <img src={img} alt="" style={{ width: 90, height: 90, objectFit: "cover", borderRadius: 12, border: "3px solid #f0d8b0" }} />
                <button type="button"
                  onClick={() => set("images", form.images.filter((_, j) => j !== i))}
                  style={{ position: "absolute", top: -8, right: -8, background: "#ef4444", border: "none", color: "white", borderRadius: "50%", width: 22, height: 22, cursor: "pointer", fontSize: 14, fontWeight: 900, lineHeight: "22px", textAlign: "center", padding: 0 }}>×</button>
              </div>
            ))}
            <div onClick={() => !compressing && fileRef.current.click()}
              style={{ width: 90, height: 90, border: "3px dashed #f0d8b0", borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", cursor: compressing ? "wait" : "pointer", color: "#c09870", fontSize: compressing ? 14 : 30, background: "#fffaf4", fontWeight: 700 }}>
              {compressing ? "..." : "+"}
            </div>
          </div>
          <input ref={fileRef} type="file" accept="image/*" multiple style={{ display: "none" }} onChange={handleImages} />
          <p style={{ fontSize: 11, color: "#b09070", fontWeight: 700, marginTop: 8 }}>
            Photos are automatically compressed to save space. Max 5 photos recommended.
          </p>
        </div>

        {/* AI Section */}
        <div className="card" style={{ marginBottom: 20, border: "2px solid #dbeafe", background: "#f0f7ff" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14, flexWrap: "wrap", gap: 10 }}>
            <div>
              <h3 style={{ fontFamily: "'Fredoka One', cursive", fontSize: 20, color: "#3b82f6" }}>AI Repair Assistant</h3>
              <p style={{ fontSize: 12, color: "#6b88b8", fontWeight: 700, marginTop: 2 }}>Step-by-step guide + parts list with store links</p>
            </div>
            {aiStage !== "questioning" && (
              <button type="button" className="btn-ai" onClick={generateAI} disabled={loadingAI || !form.title.trim()}>
                {loadingAI ? <span><span className="spinning">⟳</span> {aiStage === "idle" && isVague() ? "Analyzing..." : "Generating..."}</span> : aiStage === "done" ? "Regenerate" : "Generate"}
              </button>
            )}
          </div>

          {aiError && <div style={{ background: "#fee2e2", border: "1px solid #fecaca", color: "#dc2626", borderRadius: 10, padding: "10px 14px", fontSize: 13, fontWeight: 700, marginBottom: 12 }}>{aiError}</div>}

          {/* Clarifying questions */}
          {aiStage === "questioning" && (
            <div style={{ background: "white", borderRadius: 12, padding: 16, border: "1px solid #dbeafe" }}>
              <p style={{ fontWeight: 800, color: "#3b82f6", fontSize: 14, marginBottom: 14 }}>
                To give you a better repair guide, could you answer a few quick questions?
              </p>
              {aiQuestions.map((q, i) => (
                <div key={i} style={{ marginBottom: 14 }}>
                  <label className="field-label">{q}</label>
                  <input
                    className="field-input"
                    placeholder="Your answer (optional)"
                    value={aiAnswers[i] || ""}
                    onChange={e => setAiAnswers(a => ({ ...a, [i]: e.target.value }))}
                  />
                </div>
              ))}
              <div style={{ display: "flex", gap: 10, marginTop: 6 }}>
                <button
                  type="button"
                  className="btn-ai"
                  onClick={() => { setAiStage("answering"); generateGuide(aiQuestions.map((q, i) => `Q: ${q}\nA: ${aiAnswers[i] || "Not provided"}`).join("\n")); }}
                  style={{ flex: 1 }}
                >
                  Generate with my answers
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => generateGuide("")}
                >
                  Skip, just generate
                </button>
              </div>
            </div>
          )}

          {hasAI && aiStage !== "questioning" ? (
            <>
              <div style={{ display: "flex", borderBottom: "2px solid #dbeafe", marginBottom: 14 }}>
                <button type="button" className={`tab-btn ${aiTab === "guide" ? "tab-active-blue" : ""}`} onClick={() => setAiTab("guide")}>Repair Guide</button>
                <button type="button" className={`tab-btn ${aiTab === "parts" ? "tab-active-blue" : ""}`} onClick={() => setAiTab("parts")}>Parts ({(form.partsList || []).length})</button>
              </div>
              {aiTab === "guide" && <div style={{ fontSize: 13, color: "#334155", lineHeight: 1.85, whiteSpace: "pre-wrap", fontWeight: 600 }}>{form.repairGuide}</div>}
              {aiTab === "parts" && <PartsList parts={form.partsList || []} />}
            </>
          ) : aiStage !== "questioning" && (
            <p style={{ textAlign: "center", color: "#6b88b8", fontSize: 13, fontWeight: 700, padding: "8px 0" }}>
              Enter a title & description, then click Generate
            </p>
          )}
        </div>

        {/* Actions */}
        <div style={{ display: "flex", gap: 12 }}>
          <button type="submit" className="btn-primary" style={{ flex: 1, fontSize: 15, padding: "13px" }} disabled={saving || compressing}>
            {saving ? "Saving..." : compressing ? "Compressing photos..." : "Save Task"}
          </button>
          <button type="button" className="btn-secondary" onClick={handleCancel}>Cancel</button>
        </div>
      </form>

      {/* Unsaved Changes Modal */}
      {showUnsavedModal && (
        <div style={styles.modalBackdrop}>
          <div style={styles.modalCard}>
            <div style={{ fontSize: 40, textAlign: "center", marginBottom: 12 }}>⚠️</div>
            <h3 style={{ fontFamily: "'Fredoka One', cursive", fontSize: 22, color: "#8B4513", textAlign: "center", marginBottom: 8 }}>
              Unsaved Changes
            </h3>
            <p style={{ color: "#a07850", fontWeight: 600, fontSize: 14, textAlign: "center", marginBottom: 24, lineHeight: 1.6 }}>
              You have unsaved changes to this task. Do you want to save before leaving?
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <button
                className="btn-primary"
                style={{ width: "100%", fontSize: 15, padding: "13px" }}
                onClick={() => { setShowUnsavedModal(false); onSave(form); }}
              >
                Save Task
              </button>
              <button
                className="btn-danger"
                style={{ width: "100%", padding: "11px", fontSize: 14 }}
                onClick={() => { setShowUnsavedModal(false); onCancel(); }}
              >
                Discard Changes
              </button>
              <button
                className="btn-secondary"
                style={{ width: "100%", padding: "11px", fontSize: 14 }}
                onClick={() => setShowUnsavedModal(false)}
              >
                Keep Editing
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export function PartsList({ parts }) {
  const enabledStores = useEnabledStores() || [];
  const visibleStores = STORES.filter(s => enabledStores.includes(s.name));

  if (!parts || parts.length === 0) return <p style={{ color: "#6b88b8", fontWeight: 700, fontSize: 13 }}>No parts listed.</p>;
  const total = parts.reduce((s, p) => s + (parseFloat(p.estimatedCost) || 0) * (parseInt(p.qty) || 1), 0);
  return (
    <div>
      {parts.map((part, i) => (
        <div key={i} style={{ padding: "12px 0", borderBottom: "1px solid #e0ecff", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 140 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: "#1e3a5f" }}>{part.name}</div>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#6b88b8", marginTop: 2 }}>
              Qty: {part.qty} {part.unit} · Est. ${parseFloat(part.estimatedCost || 0).toFixed(2)}
            </div>
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {visibleStores.map(s => (
              <a key={s.name}
                href={`${s.url}${encodeURIComponent(part.searchTerm || part.name)}`}
                target="_blank" rel="noopener noreferrer"
                style={{ padding: "5px 10px", borderRadius: 6, border: `2px solid ${s.color}`, fontSize: 12, fontFamily: "'Nunito', sans-serif", fontWeight: 700, color: s.color, background: s.color + "14", textDecoration: "none", display: "inline-block" }}>
                {s.name}
              </a>
            ))}
          </div>
        </div>
      ))}
      <div style={{ marginTop: 14, textAlign: "right", fontFamily: "'Fredoka One', cursive", fontSize: 18, color: "#3b82f6" }}>
        Estimated Total: ${total.toFixed(2)}
      </div>
    </div>
  );
}

const styles = {
  grid2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 },
  modalBackdrop: {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
    zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center",
    padding: 20, animation: "fadeIn 0.2s ease",
  },
  modalCard: {
    background: "white", borderRadius: 24, padding: "32px 28px",
    width: "100%", maxWidth: 380,
    boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
  },
};
